// src/pages/consultaTheme.js
// -----------------------------------------------------------------------------
// Sistema de diseño Material 3 compartido para las páginas de Consulta
// (AddConsultaPage y ConsultaDetailPage).
//
// Idea central de Material 3: cada sección tiene un "rol de color" con un par
// { container, onContainer } que se usa para el icono/acento, más una superficie
// tonal muy suave para el contenido. Esto da jerarquía visual y color-codifica
// las secciones sin salirse de la paleta de la app.
//
// Todo se define con alpha sobre colores del tema (primary/secondary/etc.) para
// que respete automáticamente la paleta y el modo claro/oscuro.
// -----------------------------------------------------------------------------

import { alpha } from '@mui/material/styles';

// Elevación suave estilo M3 (sombras difusas, poco contraste)
export const softShadow = '0 1px 2px rgba(16,24,40,0.06), 0 1px 3px rgba(16,24,40,0.10)';
export const softShadowHover = '0 4px 12px rgba(16,24,40,0.10), 0 2px 6px rgba(16,24,40,0.08)';
export const cardShadow = '0 1px 3px rgba(16,24,40,0.08), 0 6px 20px rgba(16,24,40,0.06)';

// Radios (M3 usa esquinas más redondeadas)
export const radius = {
  sm: 12,
  md: 16,
  lg: 20,
  xl: 28,
};

// Devuelve el color base de un rol del tema de forma segura.
const roleBase = (theme, role) => {
  const p = theme.palette;
  switch (role) {
    case 'primary': return p.primary.main;
    case 'secondary': return p.secondary.main;
    case 'success': return p.success?.main || '#2e7d32';
    case 'info': return p.info?.main || '#0288d1';
    case 'warning': return p.warning?.main || '#ed6c02';
    case 'error': return p.error?.main || '#d32f2f';
    case 'tertiary': return p.secondary.main;
    default: return p.primary.main;
  }
};

// Genera el "tono de sección" M3: contenedor suave para el icono + acento.
export const sectionTone = (theme, role = 'primary') => {
  const base = roleBase(theme, role);
  const dark = theme.palette.mode === 'dark';
  return {
    accent: base,
    // Contenedor del icono (M3 "color container")
    container: alpha(base, dark ? 0.24 : 0.14),
    containerHover: alpha(base, dark ? 0.32 : 0.20),
    onContainer: base,
    // Superficie tonal del cuerpo del acordeón (M3 "surface tint")
    surface: alpha(base, dark ? 0.06 : 0.035),
    // Franja/summary cuando está expandido
    summaryOpen: alpha(base, dark ? 0.14 : 0.07),
    summaryHover: alpha(base, dark ? 0.20 : 0.11),
    border: alpha(base, dark ? 0.30 : 0.16),
  };
};

// Estilos reutilizables para un Accordion "de sección" color-codificado.
export const sectionAccordionSx = (theme, role, expanded) => {
  const t = sectionTone(theme, role);
  return {
    mb: 2,
    borderRadius: `${radius.md}px !important`,
    overflow: 'hidden',
    border: '1px solid',
    borderColor: expanded ? t.border : alpha(theme.palette.divider, 0.7),
    boxShadow: expanded ? softShadowHover : softShadow,
    transition: 'box-shadow .25s ease, border-color .25s ease',
    '&:before': { display: 'none' },
    '&:hover': { boxShadow: softShadowHover },
  };
};

export const sectionSummarySx = (theme, role, expanded) => {
  const t = sectionTone(theme, role);
  return {
    minHeight: 68,
    px: 2.5,
    backgroundColor: expanded ? t.summaryOpen : 'transparent',
    transition: 'background-color .25s ease',
    '&:hover': { backgroundColor: t.summaryHover },
    '& .MuiAccordionSummary-content': { alignItems: 'center', my: 1.5 },
    '& .MuiAccordionSummary-expandIconWrapper': { color: t.accent },
  };
};

export const sectionDetailsSx = (theme, role) => {
  const t = sectionTone(theme, role);
  return {
    p: 3,
    backgroundColor: t.surface,
    borderTop: '1px solid',
    borderColor: alpha(t.accent, theme.palette.mode === 'dark' ? 0.18 : 0.10),
  };
};

// "Pastilla" con el icono de la sección (contenedor tonal M3).
export const sectionIconBoxSx = (theme, role) => {
  const t = sectionTone(theme, role);
  return {
    width: 44,
    height: 44,
    borderRadius: `${radius.sm}px`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    bgcolor: t.container,
    color: t.onContainer,
    mr: 2,
    flexShrink: 0,
    transition: 'background-color .25s ease',
  };
};

// Cabecera con degradado tonal suave (M3 hero surface).
export const heroSurfaceSx = (theme, role = 'primary') => {
  const base = roleBase(theme, role);
  const dark = theme.palette.mode === 'dark';
  return {
    position: 'relative',
    overflow: 'hidden',
    borderRadius: `${radius.lg}px`,
    border: '1px solid',
    borderColor: alpha(base, dark ? 0.28 : 0.14),
    background: `linear-gradient(135deg, ${alpha(base, dark ? 0.22 : 0.10)} 0%, ${alpha(base, dark ? 0.08 : 0.03)} 60%, ${alpha(theme.palette.background.paper, 0)} 100%)`,
    boxShadow: cardShadow,
  };
};

// Tarjeta individual para un signo vital / dato clave.
export const statCardSx = (theme, role = 'primary') => {
  const t = sectionTone(theme, role);
  return {
    p: 2,
    height: '100%',
    borderRadius: `${radius.sm}px`,
    border: '1px solid',
    borderColor: alpha(theme.palette.divider, 0.6),
    bgcolor: theme.palette.background.paper,
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    transition: 'transform .2s ease, box-shadow .2s ease, border-color .2s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: softShadowHover,
      borderColor: t.border,
    },
  };
};
